import { useMemo } from "react";
import { PieChart } from "@mui/x-charts";

/**
 * AssistenciaTecnicaPieChart
 */

type AssistenciaTecnicaPieChartProps = {
  registradas: number;
  emAnalise: number;
  resolvidas: number;
};

export const AssistenciaTecnicaPieChart = ({
  registradas,
  emAnalise,
  resolvidas,
}: AssistenciaTecnicaPieChartProps) => {
  const total =
    registradas +
    emAnalise +
    resolvidas;

  const seriesData = useMemo(() => {
    return [
      {
        id: "registradas",
        value: registradas,
        label: "Registradas",
        color: "#ef4444",
      },
      {
        id: "em-analise",
        value: emAnalise,
        label: "Em análise",
        color: "#14b8a6",
      },
      {
        id: "resolvidas",
        value: resolvidas,
        label: "Resolvidas",
        color: "#3b82f6",
      },
    ].filter((item) => item.value > 0);
  }, [
    registradas,
    emAnalise,
    resolvidas,
  ]);

  return (
    <div className="w-full rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-black dark:text-white">Assistência Técnica</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Distribuição por status das assistência técnica</p>
        </div>

        <div className="rounded-xl bg-zinc-100 px-4 py-2 text-right dark:bg-zinc-800">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Total</p>
          <p className="text-xl font-semibold text-black dark:text-white">{total}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="flex justify-center lg:w-[400px] lg:flex-none">
          {total > 0 ? (
            <PieChart
              width={340}
              height={280}
              series={[
                {
                  data: seriesData,
                  innerRadius: 40,
                  outerRadius: 120,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  startAngle: -90,
                  endAngle: 270,
                  arcLabelMinAngle: 18,
                  arcLabel: (item) => String(item.value),
                },
              ]}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              hideLegend={true}
            />
          ) : (
            <div className="flex h-[280px] w-[340px] items-center justify-center rounded-full border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
              Sem dados para exibir
            </div>
          )}
        </div>

        <div className="grid flex-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
          {[
            {
              label: "Registradas",
              value: registradas,
              color: "bg-red-500",
              descricao: 'Quantidade de assistências técnicas registradas',
            },
            {
              label: "Em análise",
              value: emAnalise,
              color: "bg-teal-500",
              descricao: 'Quantidade de assistências técnicas em análise',
            },
            {
              label: "Resolvidas",
              value: resolvidas,
              color: "bg-blue-500",
              descricao: 'Quantidade de assistências técnicas resolvidas',
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-xl border border-zinc-200 px-4 py-3 dark:border-zinc-800"
            >
              <span className={`h-3 w-3 rounded-full ${item.color}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-black dark:text-white">{item.label}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{item.descricao}</p>
              </div>
              <strong className="text-lg text-black dark:text-white">{item.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};